// Pure, DOM-free helpers for the tier-managed patient-group guard. Kept out
// of PatientGroupPicker.vue / Customer.vue specifically so
// tests/unit/patientGroupFilters.spec.ts can exercise them without a
// component-mount harness — this repo's yarn test:unit runs under plain
// Node (see playwright.unit.config.ts), and the .vue files import via the
// "@/" webpack alias, which does not resolve there. Mirrors tierDisplay.js's
// existing split for exactly the same reason.
//
// Context: a tier owns a patient_group whose membership is rewritten by a
// nightly job. The backend rejects a manual membership edit against one
// with a 409 (patient_group_repo/addMembers.js & removeMembers.js). A group
// carries `managedByTierId` (non-null = tier-managed) on every payload the
// list endpoint returns (src/services/patient_group_repo/listGroups.js on
// the backend maps the full model, including this column, with no
// attribute exclusion).

/**
 * The set of groups selectable for a given purpose.
 *
 * - excludeManaged=false (discount attachment, voucher-batch eligibility):
 *   every group stays selectable. Binding a discount/voucher to a tier's
 *   group IS the delivery mechanism for a tier's standing discount, so
 *   hiding tier-managed groups here would break the feature entirely.
 * - excludeManaged=true (patient create/edit membership picker): strip
 *   tier-managed groups out unconditionally, so one can never be freshly
 *   selected and trigger the 409 guard on save.
 *
 * @param {Array<{id: number, managedByTierId?: number|null}>} groupList
 * @param {boolean} excludeManaged
 */
export function selectableGroups(groupList, excludeManaged) {
  return (groupList || []).filter(g => !excludeManaged || !g.managedByTierId);
}

/**
 * Names of a patient's CURRENT group memberships that are tier-managed, for
 * a read-only note next to a membership picker that excludes those groups
 * from its selectable options.
 *
 * Deliberately takes the patient's raw groupMembers list (as returned by
 * getPatient/createPatient/updatePatient — `{ groupId, group: { id, name } }`
 * rows), not whatever a picker's v-model currently holds: a picker with
 * excludeManaged=true replaces its emitted value from its own (already
 * managed-excluded) options on every change, so a tier-managed id silently
 * disappears from that value the moment the user touches the picker for an
 * unrelated group. Reading the original membership snapshot instead keeps
 * the note visible regardless of what else the user does in the form.
 *
 * @param {Array<{groupId: number, group?: {id: number, name?: string}}>} groupMembers
 * @param {Array<{id: number, managedByTierId?: number|null}>} groupList
 */
export function tierManagedMemberNames(groupMembers, groupList) {
  const managedIds = new Set(
    (groupList || []).filter(g => g.managedByTierId).map(g => g.id)
  );
  return (groupMembers || [])
    .filter(m => managedIds.has(m.groupId))
    .map(m => (m.group && m.group.name) || `#${m.groupId}`);
}
